import { SanitizeHTMLString } from "@circle-react-shared/uikit/SanitizeContent";
import { useIsAgents2BillingEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { toLocaleString } from "@circle-react/helpers/number";
import { Modal } from "@circleco/compass/components/Modal";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";

import { useHasCommunityCodeSnippets } from "../hooks/useHasCommunityCodeSnippets";

const FALLBACK_MONTHLY_AI_CREDITS = 2500;

const previewTestItems = [
  "new_opt_in_admin_preview_modal.body.point_1",
  "new_opt_in_admin_preview_modal.body.point_2",
  "new_opt_in_admin_preview_modal.body.point_3",
  "new_opt_in_admin_preview_modal.body.point_4",
] as const;

const linkSanitizeProps = {
  ADD_ATTR: ["target", "rel"],
};

export const AdminPreviewModalComponent = () => {
  const modal = useModal();
  const { currentCommunity } = usePunditUserContext();
  const hasCodeSnippets = useHasCommunityCodeSnippets();
  const isAgents2BillingEnabled = useIsAgents2BillingEnabled();
  const monthlyAiCredits =
    currentCommunity?.monthly_ai_credit_allotment ??
    FALLBACK_MONTHLY_AI_CREDITS;

  const closeModal = () => {
    void modal.hide();
    modal.remove();
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      closeModal();
    }
  };

  const bodyKey = hasCodeSnippets
    ? "new_opt_in_admin_preview_modal.body.description_with_code_snippets"
    : "new_opt_in_admin_preview_modal.body.description_without_code_snippets";

  const aiCreditsKey = isAgents2BillingEnabled
    ? "new_opt_in_admin_preview_modal.body.ai_credits_agents_2"
    : "new_opt_in_admin_preview_modal.body.ai_credits";

  return (
    <Modal.Root open={modal.visible} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t("new_opt_in_admin_preview_modal.title")} />
        <Modal.Body>
          <div className="space-y-4">
            <Typography variant="body-md" color="primary">
              <SanitizeHTMLString
                className="[&_a]:underline [&_a]:underline-offset-2"
                content={t(bodyKey)}
                sanitizeProps={linkSanitizeProps}
              />
            </Typography>
            <div>
              <Typography variant="body-md" color="primary">
                {t("new_opt_in_admin_preview_modal.body.test_heading")}
              </Typography>

              <ul className="ml-4 list-disc pl-1">
                {previewTestItems.map(itemKey => (
                  <li key={itemKey}>
                    <Typography
                      variant="body-md"
                      color="primary"
                      component="span"
                    >
                      {t(itemKey)}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
            <Typography variant="body-md" color="primary">
              <SanitizeHTMLString
                content={t("new_opt_in_admin_preview_modal.body.free_features")}
              />
            </Typography>
            <Typography variant="body-md" color="primary">
              <SanitizeHTMLString
                className="[&_a]:underline [&_a]:underline-offset-2"
                content={t(aiCreditsKey, {
                  credits_count: toLocaleString(monthlyAiCredits),
                })}
                sanitizeProps={linkSanitizeProps}
              />
            </Typography>
          </div>
        </Modal.Body>
        <Modal.Footer
          primaryAction={{
            label: t("new_opt_in_admin_preview_modal.got_it"),
            onClick: closeModal,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

const AdminPreviewModal = ModalManager.create(() => (
  <AdminPreviewModalComponent />
));

export const useAdminPreviewModal = () => useModal(AdminPreviewModal);
